import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, MapPin, Users, Building2, Truck, Fuel, Wrench, Package,
  Mountain, Wallet, CircleDollarSign, Banknote, ShoppingCart, Boxes,
  CheckSquare, PieChart, FileBarChart, LogOut, Menu, X, Smartphone,
  Settings as SettingsIcon, Calendar, Clock, ArrowRightLeft, ClipboardList
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, group: "OPS" },
  { to: "/absensi", label: "Absensi & GPS", icon: MapPin, group: "OPS" },
  { to: "/rekap-absensi", label: "Rekap Absensi", icon: ClipboardList, group: "OPS" },
  { to: "/quick", label: "Quick Input (HP)", icon: Smartphone, group: "OPS" },
  { to: "/karyawan", label: "Karyawan", icon: Users, group: "OPS" },
  { to: "/sites", label: "Site", icon: Building2, group: "OPS" },
  { to: "/holidays", label: "Hari Libur", icon: Calendar, group: "OPS" },
  { to: "/shifts", label: "Shift", icon: Clock, group: "OPS" },
  { to: "/assets", label: "Alat Berat", icon: Truck, group: "ASET" },
  { to: "/bbm", label: "BBM", icon: Fuel, group: "ASET" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, group: "ASET" },
  { to: "/sparepart", label: "Sparepart", icon: Package, group: "ASET" },
  { to: "/produksi", label: "Produksi Tambang", icon: Mountain, group: "ASET" },
  { to: "/payroll", label: "Payroll", icon: Wallet, group: "FIN" },
  { to: "/kasbon", label: "Kasbon", icon: CircleDollarSign, group: "FIN" },
  { to: "/kas-bank", label: "Kas / Bank", icon: Banknote, group: "FIN" },
  { to: "/purchasing", label: "Purchasing", icon: ShoppingCart, group: "FIN" },
  { to: "/inventory", label: "Inventory", icon: Boxes, group: "FIN" },
  { to: "/movements", label: "Barang Masuk/Keluar", icon: ArrowRightLeft, group: "FIN" },
  { to: "/approval", label: "Approval", icon: CheckSquare, group: "REPORT" },
  { to: "/budget", label: "Budget vs Aktual", icon: PieChart, group: "REPORT" },
  { to: "/laporan", label: "Laporan Site", icon: FileBarChart, group: "REPORT" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, group: "REPORT" },
];

const groups = [
  { key: "OPS", label: "Operasional" },
  { key: "ASET", label: "Aset & Produksi" },
  { key: "FIN", label: "Keuangan & Logistik" },
  { key: "REPORT", label: "Laporan & Approval" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav1 = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav1("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-40 w-64 h-screen bg-[#0A0A0A] border-r border-neutral-800 flex flex-col no-transition-all`}
        style={{transitionProperty: 'transform', transitionDuration: '200ms'}}
      >
        <div className="px-5 py-5 border-b border-neutral-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white flex items-center justify-center border border-neutral-800">
              <img src="/logo-ppp.jpg" alt="PPP" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <div className="font-display font-black text-sm leading-none">PT. PBP</div>
              <div className="font-mono text-[10px] text-[#FF6600] tracking-widest mt-1">PAPUA</div>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {groups.map((g) => (
            <div key={g.key} className="mb-4">
              <div className="px-5 mb-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{g.label}</div>
              {nav.filter((n) => n.group === g.key).map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to} to={item.to} end={item.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${item.to.replace('/', '') || 'dashboard'}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-2 text-sm no-transition-all border-l-2 ${
                        isActive
                          ? "bg-neutral-900 text-white border-[#FF6600]"
                          : "text-neutral-400 border-transparent hover:text-white hover:bg-neutral-900"
                      }`
                    }
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-neutral-800 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#FF6600] text-black flex items-center justify-center font-display font-black text-sm">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase text-neutral-500">{user?.role}</div>
            </div>
          </div>
          <button
            data-testid="logout-button" onClick={doLogout}
            className="w-full flex items-center justify-center gap-2 h-9 border border-neutral-800 hover:border-[#FF6600] hover:text-[#FF6600] text-sm font-mono uppercase no-transition-all"
          >
            <LogOut size={14} /> Keluar
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-neutral-800 flex items-center px-4 gap-3">
          <button onClick={() => setOpen(true)} data-testid="menu-toggle" className="lg:hidden"><Menu size={20} /></button>
          <div className="ml-1 font-display font-black lg:hidden">ERP TAMBANG</div>
          <div className="flex-1"></div>
          <NotificationBell />
        </header>
        <main className="flex-1 overflow-auto grid-pattern">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
